import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "utils/cn";

type TabsVariant = "underline" | "pill";

// TabsList shares its variant with the triggers inside it.
const TabsVariantContext = React.createContext<TabsVariant>("underline");

export const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>((props, ref) => <TabsPrimitive.Root ref={ref} data-ui="tabs" {...props} />);
Tabs.displayName = "Tabs";

const tabsListVariants = cva("items-center", {
  variants: {
    variant: {
      underline: "flex gap-6 border-b border-border",
      pill: "inline-flex gap-1 rounded-control bg-surface-muted p-1",
    },
  },
  defaultVariants: { variant: "underline" },
});

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant, ...props }, ref) => {
  const resolved: TabsVariant = variant || "underline";
  return (
    <TabsVariantContext.Provider value={resolved}>
      <TabsPrimitive.List
        ref={ref}
        className={cn(tabsListVariants({ variant: resolved }), className)}
        {...props}
      />
    </TabsVariantContext.Provider>
  );
});
TabsList.displayName = "TabsList";

const tabsTriggerVariants = cva(
  [
    "group/tab inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-muted-foreground",
    "transition-colors duration-fast ease-standard hover:text-foreground data-[state=active]:text-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        // -mb-px lays the 2px active bar over the list's bottom border.
        underline:
          "-mb-px h-10 rounded-t-sm border-b-2 border-transparent px-0.5 data-[state=active]:border-primary",
        pill: "h-8 rounded-[calc(var(--radius-control)-0.25rem)] px-3 data-[state=active]:bg-surface data-[state=active]:shadow-xs",
      },
    },
    defaultVariants: { variant: "underline" },
  }
);

const countClasses: Record<TabsVariant, string> = {
  underline: "bg-surface-muted",
  pill: "bg-surface",
};

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  /** Small number badge after the label, e.g. the row count of that tab. */
  count?: number;
}

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, count, children, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    >
      {children}
      {count !== undefined && (
        <span
          className={cn(
            "min-w-[1.25rem] rounded-full px-1.5 text-center text-xs font-medium leading-5 tabular-nums text-muted-foreground",
            "group-data-[state=active]/tab:bg-primary-subtle group-data-[state=active]/tab:text-primary-text",
            countClasses[variant]
          )}
        >
          {count}
        </span>
      )}
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = "TabsTrigger";

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-4 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = "TabsContent";
